/**
 * Where Caddy mounts each service on the one origin (#47). `handle_path`
 * strips the prefix, so the gate's own routes are written without it —
 * `/paymaster/:grantId` is reached at `<origin>/gate/paymaster/:grantId`.
 *
 * Its own file, apart from `deployment.ts`, because the SPA builds every URL
 * it calls from these (#161), and `deployment.ts` builds zod schemas at module
 * level. A bundler cannot prove those calls pure, so importing a path from
 * there carried all of zod into the page — weight §5.3's page should not
 * carry, for a validator the SPA never runs.
 */
export const GATE_MOUNT_PATH = "/gate";
export const INDEXER_MOUNT_PATH = "/indexer";

/**
 * The gate's routes that a caller outside it reaches (#183), each written
 * once: the gate's `mount*` functions register these, and the SPA builds its
 * URLs from them with `gatePath`. Renaming one here renames both sides, where
 * a copy on either side would pass both packages' tests and break the
 * deployed app.
 *
 * In the gate's own syntax, without the mount: `:name` is a path parameter,
 * and the literal types are what give a handler its `c.req.param("name")`.
 */
export const GATE_ROUTES = {
  /** `GET`: the deployment's public facts (#158). */
  deployment: "/deployment",
  /** JSON-RPC: the SPA's chain reads, forwarded to CDP (#159). */
  chain: "/chain",
  /** `GET`: a proof-of-work challenge (#65). */
  powChallenge: "/pow/challenge",
  /** `POST`: a solved challenge, for a genesis grant (#65). */
  genesisGrant: "/grants/genesis",
  /** `POST`: binds a grant to the account it sponsors (#163). */
  grantSender: "/grants/:grantId/sender",
  /** JSON-RPC: ERC-7677, one grant's sponsorship (#61). */
  paymaster: "/paymaster/:grantId",
} as const;

/**
 * The gate's routes with a per-IP limit (#211): the only routes Caddy hands
 * the client's address to (`Karvstokk-Client-Ip`, ADR-0007 decision 1). The
 * `mount*` functions that call `createLimiter` register their routes from
 * here — a convention, which nothing yet enforces — and `scripts/check-caddy-behaviour.mjs`
 * reads this as text and holds the Caddyfile's `@limited` matcher to exactly
 * these. Without the one list, a renamed route would pass every test and lose
 * its address in production, and with it the limit (ADR-0005).
 *
 * Each value is written as `GATE_ROUTES.<name>`, and nothing else: that is
 * what the check parses.
 */
export const GATE_LIMITED_ROUTES = {
  chain: GATE_ROUTES.chain,
  powChallenge: GATE_ROUTES.powChallenge,
  genesisGrant: GATE_ROUTES.genesisGrant,
} as const;

type GateRoute = (typeof GATE_ROUTES)[keyof typeof GATE_ROUTES];

/** The `:name` parameters a route declares, as a union of their names. */
type ParamsOf<Route extends string> =
  Route extends `${string}:${infer Name}/${infer Rest}`
    ? Name | ParamsOf<Rest>
    : Route extends `${string}:${infer Name}`
      ? Name
      : never;

/**
 * A gate route as the page reaches it: under the mount, each parameter
 * filled and escaped so it stays one path segment. Relative, because the gate
 * is on the page's own origin (#47).
 *
 * `gatePath(GATE_ROUTES.paymaster, { grantId })` →
 * `/gate/paymaster/<grantId>`. A route with parameters will not compile
 * without every one of them.
 */
export function gatePath<Route extends GateRoute>(
  route: Route,
  ...[params]: [ParamsOf<Route>] extends [never]
    ? []
    : [Readonly<Record<ParamsOf<Route>, string>>]
): string {
  const values: Readonly<Record<string, string>> = params ?? {};
  const path = route.replace(/:(\w+)/g, (_, name: string) => {
    const value = values[name];
    if (value === undefined) {
      throw new Error(`${route} needs its ${name}`);
    }
    return encodeURIComponent(value);
  });
  return `${GATE_MOUNT_PATH}${path}`;
}
